'use client';
import React, { useState } from 'react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { panoramaLivingSections, PanoramaLivingSection, PanoramaLivingTip } from '@/data/mockPanoramaLiving';
import { BookOpen, CheckCircle2 } from 'lucide-react';

const sectionFilters = [
  { label: 'All', value: 'all' },
  ...panoramaLivingSections.map((s) => ({ label: s.title, value: s.id })),
];

function TipSkeleton() {
  return (
    <div className="neon-card p-5">
      <Skeleton className="h-4 w-48 mb-3" />
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-4/5 mb-1.5" />
      <Skeleton className="h-3 w-3/5" />
    </div>
  );
}

function TipCard({ tip, color }: { tip: PanoramaLivingTip; color: string }) {
  return (
    <div className="neon-card p-5 h-full">
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: `${color}12`, border: `1px solid ${color}22` }}
        >
          {tip.type === 'checklist' ? (
            <CheckCircle2 size={14} style={{ color }} />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          )}
        </div>
        <h4 className="font-display font-semibold text-[14px] leading-snug" style={{ color: '#F2F4FF' }}>
          {tip.title}
        </h4>
      </div>

      {tip.body && (
        <p className="text-[13px] leading-relaxed mb-3 pl-11" style={{ color: '#A7A3C4' }}>
          {tip.body}
        </p>
      )}

      {tip.checklist && tip.checklist.length > 0 && (
        <ul className="space-y-2 pl-11 mb-3">
          {tip.checklist.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <div
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: `${color}12`, border: `1px solid ${color}22` }}
              >
                <div className="w-1 h-1 rounded-full" style={{ background: color }} />
              </div>
              <span className="text-[13px] leading-relaxed" style={{ color: '#A7A3C4' }}>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {tip.tags && tip.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-11">
          {tip.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-[11px] px-2 py-0.5 rounded"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionBlock({ section }: { section: PanoramaLivingSection }) {
  return (
    <div id={section.id}>
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-[20px] flex-shrink-0"
          style={{ background: `${section.color}12`, border: `1px solid ${section.color}22` }}
        >
          {section.icon}
        </div>
        <div>
          <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
            {section.title}
          </h2>
          <p className="text-[13px]" style={{ color: '#A7A3C4' }}>{section.intro}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {section.tips.map((tip) => (
          <TipCard key={tip.id} tip={tip} color={section.color} />
        ))}
      </div>
    </div>
  );
}

export default function PanoramaLivingPage() {
  const [activeSection, setActiveSection] = useState('all');
  const [isLoading] = useState(false);

  const visibleSections = activeSection === 'all'
    ? panoramaLivingSections
    : panoramaLivingSections.filter((s) => s.id === activeSection);

  const totalTips = panoramaLivingSections.reduce((acc, s) => acc + s.tips.length, 0);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Resident Resource"
          title="Panorama Living"
          description="An insider handbook for elevated living at Panorama Towers. Practical, curated, and written for residents who want to get the most out of this building."
          stat={{ value: String(totalTips), label: 'tips & guides' }}
        />

        {/* Section nav */}
        <div style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {panoramaLivingSections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150 hover:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <span>{s.icon}</span>
                  <span className="hidden sm:inline">{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="mb-8">
            <FilterRow filters={sectionFilters} active={activeSection} onChange={setActiveSection} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, i) => <TipSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && visibleSections.length === 0 && (
            <EmptyState
              title="No content found"
              description="Try a different section filter."
              icon={<BookOpen size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveSection('all')}>
                  Show all sections
                </button>
              }
            />
          )}

          {!isLoading && visibleSections.length > 0 && (
            <div className="space-y-14">
              {visibleSections.map((section, idx) => (
                <div key={section.id}>
                  <SectionBlock section={section} />
                  {idx < visibleSections.length - 1 && (
                    <div className="neon-divider mt-14" />
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            className="mt-14 px-5 py-4 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-start gap-3">
              <BookOpen size={16} style={{ color: 'rgba(167,163,196,0.40)', marginTop: 2, flexShrink: 0 }} />
              <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.50)' }}>
                Panorama Living is a resident-curated guide. Content reflects resident experience and community knowledge. Product and service recommendations are not sponsored unless noted. Verify current availability and pricing directly.
              </p>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
